import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Menu,
  MenuButton,
  Text,
  HStack,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuOptionGroup,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';

import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { NewNodeDisplay } from './commonNodeDisplay';
import { generatedNodes, operationNodes, rawNodes } from './nodes';

type EditModeControlsProps = {
  onClearGraph: () => void;
  onMigrateXlsx2Csv: () => void;
  onResetToLatest: () => void;
  onUndoChanges: () => void;
  filtered: boolean;
};

const nodeControls = [
  {
    headingText: 'Raw Tapes',
    nodes: rawNodes,
  },
  {
    headingText: 'Operations',
    nodes: operationNodes,
  },
  {
    headingText: 'Generated Tapes',
    nodes: generatedNodes,
  },
];

const NodeSubmenu = ({
  control,
}: {
  control: { headingText: string; nodes: TapeBuilderUI.TapeBuilderNodeType[] };
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    onOpen();
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      onClose();
    }, 300);
  };

  const handlePopoverMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      onClose();
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="right-start"
      isLazy
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <MenuItem
          icon={<ChevronRightIcon />}
          closeOnSelect={false}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          Add {control.headingText}
        </MenuItem>
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        maxW="600px"
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      >
        <PopoverBody>
          <MenuOptionGroup title="Drag on Canvas to add" type="radio">
            <MenuDivider />
            <Grid templateColumns="repeat(3, 1fr)">
              {control.nodes.map((nodeType) => {
                const nodeInterface = TapeBuilderUI.getUINodeByType(nodeType);
                return (
                  <Box key={`newNodeDisplay-${nodeType}`}>
                    <NewNodeDisplay
                      nodeText={nodeInterface.nodeTypeText}
                      nodeType={nodeType}
                    />
                    <MenuDivider />
                  </Box>
                );
              })}
            </Grid>
          </MenuOptionGroup>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const EditModeControls = ({
  onClearGraph,
  onResetToLatest,
  onUndoChanges,
  onMigrateXlsx2Csv,
  filtered,
}: EditModeControlsProps) => {
  return (
    <Box ml="2">
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="ghost"
          fontWeight="normal"
          _hover={{ bg: 'gray.100' }}
        >
          <HStack spacing={1}>
            <Text fontSize="sm">Edit Menu</Text>
            <ChevronDownIcon />
          </HStack>
        </MenuButton>
        <MenuList>
          {/* Graph Actions Section */}
          <MenuItem onClick={onMigrateXlsx2Csv}>Migrate Xlsx2Csv</MenuItem>
          <MenuItem onClick={onClearGraph}>
            {filtered ? 'Clear Filtered' : 'Clear all'}
          </MenuItem>
          <MenuItem onClick={onUndoChanges}>Undo Changes</MenuItem>
          <MenuItem onClick={onResetToLatest}>
            Reset to Latest Committed Graph
          </MenuItem>

          <MenuDivider />

          {/* Add Nodes Section - with nested submenus using Popover */}
          {nodeControls.map((control) => (
            <NodeSubmenu key={control.headingText} control={control} />
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};
